/**
 * Routes de contenu (DECISIONS §4.2, `docs/api-v1.md` §8.3).
 *
 * Le contenu est **public au sens du contrat** mais exige un jeton : aucune donnée de jeu n’est
 * servie sans compte (cahier des charges, DECISIONS §4.2). Les valeurs d’équilibrage sont
 * exposées pour la simulation locale ; la sécurité repose sur la validation serveur (§8.7).
 *
 * Depuis M2, les ennemis viennent du **manifeste** (`manifest.ennemis`, DECISIONS §1.3 A-2) : le
 * client ne lit plus aucun fichier de contenu embarqué, l'ancien module de chargement local a
 * disparu.
 */
import {
  levelResponseSchema,
  manifestSchema,
  type ContenuSimulation,
  type Manifest,
} from '@elemora/shared/api';
import { API_PATHS } from '@elemora/shared/constants';

import { requete } from './http';

import type { Niveau } from '@elemora/shared/content';

/** `GET /content/manifest` : tours, ennemis, améliorations, cartes, niveaux et équilibrage. */
export async function chargerManifeste(): Promise<Manifest> {
  return requete({ methode: 'GET', chemin: API_PATHS.content.manifest, schema: manifestSchema });
}

/** `GET /content/levels/:id` : parcours, emplacements et vagues d’un niveau. */
export async function chargerNiveau(levelId: string): Promise<Niveau> {
  const reponse = await requete({
    methode: 'GET',
    chemin: API_PATHS.content.level(levelId),
    schema: levelResponseSchema,
  });
  return reponse.level;
}

/**
 * Assemble le contenu nécessaire à la simulation locale (`ContenuSimulation`, contrat
 * d’intégration de `<GameCanvas>` livré par M1-05).
 *
 * Les ennemis sont ceux du manifeste publié : si le contenu change, le client simule avec la
 * version que le serveur vient de lui donner, jamais avec un fichier figé dans le bundle.
 */
export function composerContenuSimulation(manifeste: Manifest, niveau: Niveau): ContenuSimulation {
  return {
    tours: [...manifeste.tours],
    ennemis: [...manifeste.ennemis],
    niveau,
    balance: manifeste.balance,
  };
}

/**
 * Vrai si toutes les vagues du niveau désignent un ennemi du manifeste.
 *
 * Contrôle de cohérence **client** : il ne remplace pas la validation serveur, il évite seulement
 * d'afficher un plateau vide quand le contenu publié est incohérent.
 */
export function vaguesCouvertes(
  manifeste: Manifest,
  vagues: readonly { groupes: readonly { ennemiId: string }[] }[],
): boolean {
  const connus = new Set(manifeste.ennemis.map((ennemi) => ennemi.id));
  return vagues.every((vague) => vague.groupes.every((groupe) => connus.has(groupe.ennemiId)));
}
